import React, { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { DayPicker, MonthCaption } from 'react-day-picker'
import { ja, enUS } from 'react-day-picker/locale'
import { DayPickerProps } from './types'
import { Day } from './elements/Day'
import { Month } from './elements/Month'
import { Nav } from './elements/Nav'
import { DayButton } from './elements/DayButton'

export const DatePicker = (props: DayPickerProps) => {
  const [selected, setSelected] = useState<Date>()
  const [currentMonth, setCurrentMonth] = useState<Date | undefined>(props.initDate)
  const range = props.range || 7
  const data = props.data
  const isFull = props.full
  const holidays = JSON.parse((document.querySelector('[data-holidays]') as HTMLElement)?.dataset?.holidays || '[]') as string[]

  useEffect(() => {
    setSelected(props.initDate)

    if (props.initDate) {
      setCurrentMonth(props.initDate)
      return
    }
    // Set month to calendar if select_month is exists
    if (!isFull && data.length) {
      const urlParams = new URLSearchParams(window.location.search)
      const selectedMonth = (urlParams.get('select_month') || '').split(',').sort()[0] // "2025/08,2025/06,2025/10"
      const [year, monthStr] = selectedMonth.split('/')
      const date = new Date(parseInt(year), parseInt(monthStr) - 1)
      const startMonth = getStartMonth()
      setCurrentMonth(date >= startMonth ? date : startMonth)
    }
  }, [props.initDate, props.data])

  const getPrice = (date: Date): string => {
    const dateFormatter = Intl.DateTimeFormat('sv-SE')
    const dateString = dateFormatter.format(date)
    const price = data.find((item) => item.date === dateString)?.price
    if (!price) {
      return ''
    }

    const man = parseInt(price) / 10000
    return String(man % 1 === 0 ? man.toFixed() : man.toFixed(1))
  }

  const getEndRange = () => {
    if (selected) {
      return new Date(selected.getTime() + (range - 1) * 24 * 60 * 60 * 1000)
    }

    return undefined
  }

  const getStartMonth = () => {
    return data[0] && new Date(data[0].date)
  }

  const getEndMonth = () => {
    const lastItem = data[data.length - 1]
    return lastItem && new Date(new Date(lastItem.date).getTime() + (range - 1) * 24 * 60 * 60 * 1000)
  }

  const getNumberOfMonths = () => {
    if (isFull && data.length > 0) {
      const first = getStartMonth()
      const last = getEndMonth()
      return last.getMonth() - first.getMonth() + 1 + (last.getFullYear() - first.getFullYear()) * 12
    }
    if (isFull && data.length === 0) {
      const urlParams = new URLSearchParams(window.location.search)
      const selectedMonth = (urlParams.get('select_month') || '').split(',').sort()
      const lastSelectedMonth = selectedMonth[selectedMonth.length - 1]
      if (lastSelectedMonth) {
        const lastSelectedMonthNumber = parseInt(lastSelectedMonth.split('/')[1])
        const currentMonthNumber = new Date().getMonth() + 1
        return Math.max(lastSelectedMonthNumber - currentMonthNumber + 2, 1)
      }
    }

    return 1
  }

  const modifiers = {
    listed: data.map((item) => new Date(item.date)),
    available: data.filter((item) => item.available).map((item) => new Date(item.date)),
    ...(selected
      ? {
          inRange: { from: selected, to: getEndRange() },
          startRange: selected,
          endRange: getEndRange(),
        }
      : {}),
    holiday: holidays.map((item) => new Date(item)),
  }

  const Blank = () => {
    return <></>
  }

  const handleOnSelect = (value) => {
    setSelected(value)
    props.onChangeDataSearch && props.onChangeDataSearch('start_date', value, 'text')
  }

  // Handle month change in the calendar
  const handleMonthChange = (month: Date) => {
    setCurrentMonth(month)
  }

  return (
    <>
      <DayPicker
        className="bg-white py-4 px-2"
        mode="single"
        locale={document.documentElement.lang === 'ja' ? ja : enUS}
        selected={selected}
        modifiers={modifiers}
        onSelect={handleOnSelect}
        month={isFull ? getStartMonth() : currentMonth || getStartMonth()}
        onMonthChange={handleMonthChange}
        startMonth={getStartMonth()}
        endMonth={getEndMonth()}
        numberOfMonths={getNumberOfMonths()}
        components={{
          Nav: isFull ? Blank : Nav,
          Month,
          Day,
          DayButton: (props) => <DayButton getPrice={getPrice} isShowIcon={!isFull} {...props} />,
          MonthCaption: isFull ? (props) => <MonthCaption {...props} className="mb-3 font-bold pb-3 mt-2 font-size-20" /> : Blank,
        }}
        formatters={{
          formatCaption: (date, options) =>
            format(date, document.documentElement.lang === 'ja' ? 'yyyy年LLLL' : 'LLLL yyyy', options),
        }}
        {...props}
      />
    </>
  )
}
